<template>
    <div>
        <form-row>
            <form-cell width="half">
                <suggest-field
                    :label="$t('forms.referrals.make.fields.patient_first_name.label')"
                    :placeholder="$t('forms.referrals.make.fields.patient_first_name.placeholder')"
                    :suggestionsCall="patientSuggestionsCall"
                    :isRequired="true"
                    :isDisabled="isDisabled"
                    :isSuggested="isSuggested"
                    :formData="formData"
                    :formPrefills="formPrefills"
                    valuePath="patient.firstName"
                    @change="change($event)"
                    @select="changePatientData($event)"
                    @hoverSuggestion="prefillPatientData($event)"
                ></suggest-field>
            </form-cell>

            <form-cell width="quarter">
                <text-field
                    :label="$t('forms.referrals.make.fields.middle_name.label')"
                    :placeholder="$t('forms.referrals.make.fields.middle_name.placeholder')"
                    :isRequired="false"
                    :isDisabled="isDisabled"
                    :isSuggested="isSuggested"
                    :formData="formData"
                    :formPrefills="formPrefills"
                    valuePath="patient.middleName"
                    @change="change($event)"
                ></text-field>
            </form-cell>

            <form-cell width="half">
                <suggest-field
                    :label="$t('forms.referrals.make.fields.patient_last_name.label')"
                    :placeholder="$t('forms.referrals.make.fields.patient_last_name.placeholder')"
                    :suggestionsCall="patientSuggestionsCall"
                    :isSuggested="isSuggested"
                    :isRequired="true"
                    :isDisabled="isDisabled"
                    :formData="formData"
                    :formPrefills="formPrefills"
                    valuePath="patient.lastName"
                    @change="change($event)"
                    @hoverSuggestion="prefillPatientData($event)"
                    @select="changePatientData($event)"
                ></suggest-field>
            </form-cell>

            <form-cell width="quarter">
                <text-field
                    :label="$t('forms.referrals.make.fields.patient_ssn.label')"
                    :placeholder="$t('forms.referrals.make.fields.patient_ssn.placeholder')"
                    :isRequired="true"
                    :isDisabled="isDisabled"
                    :isSuggested="isSuggested"
                    :formData="formData"
                    :formPrefills="formPrefills"
                    valuePath="patient.ssn"
                    @change="change($event)"
                ></text-field>
            </form-cell>
        </form-row>

        <form-row align="stretch">
            <form-cell width="half">
                <birth-date-field
                    :label="$t('forms.referrals.make.fields.patient_birth_date.label')"
                    :isSuggested="isSuggested"
                    :isRequired="true"
                    :isDisabled="isDisabled"
                    :formData="formData"
                    :formPrefills="formPrefills"
                    valuePath="patient.dateOfBirth"
                    @change="change($event)"
                ></birth-date-field>
            </form-cell>

            <form-cell width="half">
                <phone-field
                    :label="$t('forms.referrals.make.fields.patient_mobile_phone.label')"
                    :placeholder="$t('common.phone_number.placeholder')"
                    :hint="$t('forms.referrals.make.fields.patient_mobile_phone.info')"
                    :isRequired="true"
                    :isSuggested="isSuggested"
                    :isDisabled="isDisabled"
                    :formData="formData"
                    :formPrefills="formPrefills"
                    valuePath="patient.mobilePhone"
                    @change="change($event)"
                    @country-code-change="emitEvent({ name: 'country-code-change', payload: $event })"
                ></phone-field>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="half">
                <text-field
                    :label="$t('forms.referrals.make.fields.email.label')"
                    :placeholder="$t('forms.referrals.make.fields.email.placeholder')"
                    :isRequired="false"
                    :isDisabled="isDisabled"
                    :isSuggested="isSuggested"
                    :formData="formData"
                    :formPrefills="formPrefills"
                    valuePath="patient.email"
                    @change="change($event)"
                ></text-field>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="half">
                <switch-input
                    :label="'Add patient address'"
                    :isLabelOnLeft="false"
                    :value="showPatientAddress"
                    @change="togglePatientAddress"
                ></switch-input>
            </form-cell>
        </form-row>

        <form-row v-if="showPatientAddress">
            <form-cell width="half">
                <suggest-field
                    :label="$t('forms.referrals.search.fields.patient_address.label')"
                    :placeholder="$t('forms.referrals.search.fields.patient_address.placeholder')"
                    :suggestionsCall="addressSuggestionCall"
                    :isRequired="showPatientAddress"
                    :isDisabled="isDisabled"
                    :isSuggested="isSuggested"
                    :formData="formData"
                    valuePath="patient.address"
                    :formPrefills="formPrefills"
                    @change="change($event)"
                    @select="changeLocationAddress($event)"
                ></suggest-field>
            </form-cell>
            <form-cell width="half">
                <text-field
                    :label="$t('forms.referrals.search.fields.patient_city.label')"
                    :placeholder="$t('forms.referrals.search.fields.patient_city.placeholder')"
                    :isRequired="showPatientAddress"
                    :isDisabled="isDisabled"
                    :isSuggested="isSuggested"
                    :formData="formData"
                    :formPrefills="formPrefills"
                    valuePath="patient.city"
                    @change="change($event)"
                ></text-field>
            </form-cell>
        </form-row>

        <form-row v-if="showPatientAddress">
            <form-cell width="half">
                <select-field
                    :label="$t('forms.referrals.search.fields.patient_state.label')"
                    :placeholder="$t('forms.referrals.search.fields.patient_state.placeholder')"
                    :options="states"
                    :isRequired="showPatientAddress"
                    :isDisabled="isDisabled"
                    :isSuggested="isSuggested"
                    :formData="formData"
                    valuePath="patient.state"
                    :formPrefills="formPrefills"
                    @change="change($event)"
                ></select-field>
            </form-cell>
            <form-cell width="half">
                <text-field
                    :label="$t('forms.referrals.search.fields.patient_zipcode.label')"
                    :placeholder="$t('forms.referrals.search.fields.patient_zipcode.placeholder')"
                    :isRequired="showPatientAddress"
                    :isDisabled="isDisabled"
                    :isSuggested="isSuggested"
                    :formData="formData"
                    :formPrefills="formPrefills"
                    valuePath="patient.zip"
                    @change="change($event)"
                ></text-field>
            </form-cell>
        </form-row>
        <form-row v-if="showPatientAddress">
            <text-field
                :label="$t('forms.referrals.search.fields.patient_floor_number.label')"
                :placeholder="$t('forms.referrals.search.fields.patient_floor_number.placeholder')"
                :isRequired="false"
                :isDisabled="isDisabled"
                :isSuggested="isSuggested"
                :formData="formData"
                :formPrefills="formPrefills"
                valuePath="patient.floorNumber"
                @change="change($event)"
            ></text-field>
            <text-field
                :label="$t('forms.referrals.search.fields.patient_apartment_number.label')"
                :placeholder="$t('forms.referrals.search.fields.patient_apartment_number.placeholder')"
                :isRequired="false"
                :isDisabled="isDisabled"
                :isSuggested="isSuggested"
                :formData="formData"
                :formPrefills="formPrefills"
                valuePath="patient.apartmentNumber"
                @change="change($event)"
            ></text-field>
            <text-field
                :label="$t('forms.referrals.search.fields.patient_building_number.label')"
                :placeholder="$t('forms.referrals.search.fields.patient_building_number.placeholder')"
                :isRequired="false"
                :isDisabled="isDisabled"
                :isSuggested="isSuggested"
                :formData="formData"
                :formPrefills="formPrefills"
                valuePath="patient.buildingNumber"
                @change="change($event)"
            ></text-field>
        </form-row>
    </div>
</template>

<script>
    import dayjs from 'dayjs'
    import StatesAPI from 'api/StatesAPI'
    import FormFragmentMixin from 'mixins/FormFragmentMixin'
    import FormChangeableMixin from 'mixins/FormChangeableMixin'
    import PracticesAPI from 'api/PracticesAPI'
    import { composeDisplayName } from 'components/common/CommonUserName'
    import cloneDeep from 'lodash.clonedeep'

    export default {
        mixins: [FormFragmentMixin, FormChangeableMixin],

        props: {
            practiceId: { type: [String, Number], default: 0 },
        },

        data() {
            return {
                formPrefills: {},
                isSuggested: false,
                autocompleteService: null,
                states: [],
                showPatientAddress: false,
            }
        },

        computed: {
            patientSuggestionsCall() {
                return (query) => {
                    if (this.$auth.isUserPracticeDemo()) {
                        const demoPatient = {
                            id: 1564,
                            firstName: 'John',
                            lastName: 'Smith',
                            dateOfBirth: '1964-03-05',
                            mobilePhone: '3125202070',
                        }

                        return Promise.resolve([
                            {
                                label: this.$t('forms.referrals.make.fields.patient_first_name.born_on', {
                                    name: `${demoPatient.firstName} ${demoPatient.lastName}`,
                                    date: dayjs(demoPatient.dateOfBirth).format('MMM DD YYYY'),
                                }),
                                value: demoPatient,
                            },
                        ])
                    } else {
                        return PracticesAPI.getPatients(this.practiceId ?? 0, query).then((patient) =>
                            patient.map((patient) => ({
                                label: this.$t('forms.referrals.make.fields.patient_first_name.born_on', {
                                    name: composeDisplayName(patient),
                                    date: dayjs(patient.dateOfBirth).format('MMM DD YYYY'),
                                }),
                                value: patient,
                            })),
                        )
                    }
                }
            },

            addressSuggestionCall() {
                //eslint-disable-next-line no-undef
                const autocompleteService = new google.maps.places.AutocompleteService()
                return (query) => {
                    const request = {
                        input: query,
                        componentRestrictions: { country: 'US' },
                    }
                    return autocompleteService.getPlacePredictions(request).then((results) => {
                        return results?.predictions?.map((item) => {
                            return {
                                label: item.description,
                                value: item.place_id,
                            }
                        })
                    })
                }
            },
        },

        methods: {
            togglePatientAddress() {
                this.showPatientAddress = !this.showPatientAddress
                this.$emit('address-toggle', this.showPatientAddress)
            },
            changePatientData({ value: patient } = {}) {
                if (!patient) {
                    return
                }

                this.change({
                    path: 'patient',
                    value: patient,
                })

                this.change({
                    path: 'patientId',
                    value: patient.id,
                })
            },

            prefillPatientData({ value: patient } = {}) {
                this.formPrefills = { patient }
            },

            changeLocationAddress(payload) {
                if (!payload?.value) {
                    return
                }
                //eslint-disable-next-line no-undef
                const placesService = new google.maps.places.PlacesService(document.createElement('div'))
                const request = {
                    placeId: payload.value,
                    fields: ['address_components'],
                }

                placesService.getDetails(request, (place, status) => {
                    //eslint-disable-next-line no-undef
                    if (status === google.maps.places.PlacesServiceStatus.OK) {
                        const addressComponents = {}
                        place.address_components.forEach((component) => {
                            component.types.forEach((type) => {
                                switch (type) {
                                    case 'street_number':
                                        addressComponents.streetNumber = component.long_name
                                        break
                                    case 'route':
                                        addressComponents.address = component.long_name
                                        break
                                    case 'locality':
                                        addressComponents.city = component.long_name
                                        break
                                    case 'administrative_area_level_1':
                                        addressComponents.state = component.short_name
                                        break
                                    case 'postal_code':
                                        addressComponents.zipCode = component.long_name
                                        break
                                }
                            })
                        })

                        const patient = cloneDeep(this.formData.patient)
                        patient.city = addressComponents.city || ''
                        patient.state = addressComponents.state || ''
                        patient.zip = addressComponents.zipCode || ''
                        patient.address = `${addressComponents.streetNumber || ''} ${addressComponents.address || ''}`
                        this.$set(this.formData, 'patient', patient)
                    }
                })
            },
        },

        mounted() {
            this.$on('local:beforeChange', (path) => {
                if (path === 'patient') {
                    return
                }

                this.isSuggested = false
            })
            StatesAPI.getList().then((res) => (this.states = res))
        },
    }
</script>
